import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Set up axios interceptor to add auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: getAuthHeader()
      });
      setUser(data);

      // Auto-update timezone if it changed (e.g. user traveled)
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && browserTz !== data.timezone) {
        axios.put(`${BACKEND_URL}/api/auth/timezone`, { timezone: browserTz }, {
          headers: getAuthHeader()
        }).catch(() => {});
      }
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await axios.post(
      `${BACKEND_URL}/api/auth/login`,
      { email, password, timezone: tz }
    );

    // If 2FA is required, return the response for the UI to handle
    if (data.requires_2fa) {
      return data;
    }

    // Direct login (fallback if 2FA not enabled)
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, user_tag: data.user_tag || "", role: data.role });
    return data;
  };

  const verifyLogin = async (email, code) => {
    const { data } = await axios.post(
      `${BACKEND_URL}/api/auth/verify-login`,
      { email, code }
    );
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, user_tag: data.user_tag || "", role: data.role });
    return data;
  };

  const register = async (email, password, name, userTag) => {
    const { data } = await axios.post(
      `${BACKEND_URL}/api/auth/register`,
      { email, password, name, user_tag: userTag }
    );
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, user_tag: data.user_tag || "", role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { headers: getAuthHeader() });
    } catch (e) {
      // Logout cleanup — server error is non-critical
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLogin, register, logout, checkAuth, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
